import { useState, useMemo, useEffect, useRef } from "react";
import JobDescription from "./upload_jd";
import RoleMapping from "./Search_mapping";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { useForm } from "react-hook-form";
import SearchCandidates from "./search_candidatedt";
import Select from "react-select";

export default function NewSearchCandidates() {
  const [activeTab, setActiveTab] = useState("jd");
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showCompensation, setShowCompensation] = useState(false);
  const { setGlobalLoading, token } = useOutletContext();
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState([]);
  const [selectedDesignation, setSelectedDesignation] = useState(null);

  const [functions, setFunctions] = useState([]);
  const [country, setCountry] = useState([]);
  const [Industries, setIndustries] = useState([]);

  const [gptFunction, setGptFunction] = useState(null);
  const [gptIndustry, setGptIndustry] = useState(null);
  const [gpt_country, setGptCountry] = useState(null);
  const [gptDesignation, setGptDesignation] = useState(null);
  const designationDebounceRef = useRef(null);
  const [designationError, setDesignationError] = useState("");
  const [searchTrigger, setSearchTrigger] = useState("");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef(null);
  const [gptRelatedCountries, setGptRelatedCountries] = useState([]);
  const [gptRelatedDesignations, setGptRelatedDesignations] = useState([]);

  const candidates = useMemo(() => {
    if (!hasSearched) return [];
    return new Array(12).fill(null);
  }, [hasSearched]);

  const handleSearch = () => {
    setIsSearching(true);
    setTimeout(() => {
      setHasSearched(true);
      setIsSearching(false);
    }, 800);
  };

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm();
  // ---------------- RESET FUNCTION ----------------
  const resetFilters = () => {
    // React state
    setSelectedFunction("");
    setSelectedDesignation("");
    setSelectedCountry([]);
    setSelectedIndustry([]);

    // GPT values
    setGptFunction(null);
    setGptIndustry(null);
    setGptCountry(null);
    setGptDesignation(null);
    setGptRelatedCountries([]);

    // Errors
    setDesignationError("");
    clearErrors();

    // react-hook-form
    setValue("designation", "");
    setValue("functions", "");
    setValue("country", []);
    setValue("industry", []);
  };

  // ---------------- TAB CHANGE EFFECT ----------------
  useEffect(() => {
    resetFilters();
  }, [activeTab]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      setGlobalLoading?.(true);
      try {
        const [funcRes, countryRes, industryRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/common_controller/get_major_functions`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/common_controller/get_countries_list`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(
            `${API_BASE_URL}/common_controller/get_master_industry_list`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
        ]);
        setFunctions(funcRes.data.options || []);
        setCountry(countryRes.data.options || []);
        setIndustries(industryRes.data.options || []);
      } catch (err) {
        console.error("Error fetching options:", err);
      } finally {
        setGlobalLoading?.(false);
      }
    })();
  }, [token, API_BASE_URL, setGlobalLoading]);

  const relatedCountriesOptions = useMemo(() => {
    const normalizeCountry = (str = "") =>
      str
        .toLowerCase()
        .replace(/\([^)]*\)/g, "")
        .trim();

    return country
      .filter((c) => {
        const countryLabel = normalizeCountry(c.label);

        return gptRelatedCountries?.some((gptCountry) => {
          const normalizedGPT = normalizeCountry(gptCountry);

          return (
            countryLabel.includes(normalizedGPT) ||
            normalizedGPT.includes(countryLabel)
          );
        });
      })
      .map((c) => ({
        label: c.label,
        value: c.value,
      }));
  }, [country, gptRelatedCountries]);

  useEffect(() => {
    // Normalize both sides to string before comparing — resolved IDs
    // from the backend arrive as JS numbers, but <select>/react-select
    // option values are commonly strings, so strict-equality/array
    // .includes() checks were silently failing on every match.
    const asStr = (v) => String(v);
    const gptIndustryArr = (
      Array.isArray(gptIndustry) ? gptIndustry : [gptIndustry]
    )
      .filter((v) => v != null)
      .map(asStr);
    const gptCountryArr = (
      Array.isArray(gpt_country) ? gpt_country : [gpt_country]
    )
      .filter((v) => v != null)
      .map(asStr);

    /* ---------- FUNCTION ---------- */
    const matchedFunction = functions.find(
      (c) =>
        asStr(gptFunction) === asStr(c.label) ||
        asStr(gptFunction) === asStr(c.value),
    );

    if (matchedFunction) {
      setValue("functions", matchedFunction.value);
    }

    /* ---------- INDUSTRY ---------- */
    const selectedIndustriesObj = Industries.filter(
      (c) =>
        gptIndustryArr.includes(asStr(c.label)) ||
        gptIndustryArr.includes(asStr(c.value)),
    );

    if (selectedIndustriesObj.length > 0) {
      setSelectedIndustry(selectedIndustriesObj);
      setValue(
        "industry",
        selectedIndustriesObj.map((c) => c.value),
      );
    }

    /* ---------- COUNTRY ---------- */
    const selectedCountriesObj = country.filter(
      (c) =>
        gptCountryArr.includes(asStr(c.label)) ||
        gptCountryArr.includes(asStr(c.value)),
    );

    if (selectedCountriesObj.length > 0) {
      setSelectedCountry(selectedCountriesObj);
      setValue(
        "country",
        selectedCountriesObj.map((c) => c.value),
      );
    }

    /* ---------- DESIGNATION ---------- */
    if (gptDesignation) {
      setValue("designation", gptDesignation);
    }
  }, [
    gptFunction,
    gptIndustry,
    gpt_country,
    gptDesignation,
    functions,
    Industries,
    country,
    gptRelatedCountries,
    gptRelatedDesignations,
  ]);

  const handleDesignationChange = (value) => {
    setSelectedDesignation(value);
    setDesignationError("");

    clearTimeout(designationDebounceRef.current);

    if (!value || value.trim().length < 1) return;

    designationDebounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/ai_candidates/get_keyword_options`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ keywords: value }),
          },
        );

        const data = await response.json();

        if (!data.keywords || data.keywords.length === 0) {
          setSelectedDesignation("");
          setValue("designation", "");
          setDesignationError("No designation found");
        } else {
          clearErrors("designation");
          setValue("designation", value);
        }
      } catch (err) {
        console.error("Designation validation failed", err);
      }
    }, 500);
  };

  return (
    <>
      <div className="content">
        <main className="container-fluid py-4">
          <div className="row g-4">
            {/* FILTERS */}
            {showFilters && (
              <aside className="col-md-3">
                {activeTab !== "mapping" && activeTab !== "search" && (
                  <div className="card p-4 mb-3" style={{ top: -23 }}>
                    <h6 className="fw-semibold mb-3">
                      <i className="ti ti-filter text-warning me-1" />
                      Filters
                    </h6>

                    <div className="mb-3">
                      <label className="form-label small text-uppercase">
                        Designation
                      </label>
                      <input
                        className="form-control"
                        type="text"
                        placeholder="Designation"
                        {...register("designation")}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value) {
                            setSelectedDesignation(gptDesignation);
                            return;
                          }
                          setSelectedDesignation(e.target.value);
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label small text-uppercase">
                        Function
                      </label>
                      <select
                        name="functions"
                        {...register("functions")}
                        className="form-select custom-functionselect"
                        onChange={(e) => {
                          setSelectedFunction(e.target.value);
                        }}
                      >
                        <option value="">Select Function</option>
                        {functions.length > 0
                          ? functions.map((value, index) => (
                              <option key={index} value={value.value}>
                                {value.label}
                              </option>
                            ))
                          : ""}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label small text-uppercase">
                        Country
                      </label>
                      <Select
                        isMulti
                        closeMenuOnSelect={false} // keep open after selecting
                        hideSelectedOptions={true} // keep selected options hide
                        blurInputOnSelect={false} // keep cursor in search box
                        isClearable={true}
                        options={country}
                        value={selectedCountry}
                        placeholder="Select Country"
                        onChange={(selected) => {
                          setSelectedCountry(selected || []);

                          setValue(
                            "country",
                            selected ? selected.map((item) => item.value) : [],
                          );
                        }}
                      />

                      {relatedCountriesOptions.length > 0 && (
                        <div className="mt-2">
                          <span className="fw-semibold">
                            Related Countries:
                          </span>

                          {relatedCountriesOptions.map((item) => (
                            <span
                              key={item.value}
                              onClick={() => {
                                setSelectedCountry((prev) => {
                                  const exists = prev.some(
                                    (country) => country.value === item.value,
                                  );

                                  if (exists) return prev;

                                  const updated = [...prev, item];

                                  setValue(
                                    "country",
                                    updated.map((x) => x.value),
                                  );

                                  return updated;
                                });
                              }}
                              className="badge badge-secondary badge-xs me-1"
                              style={{ cursor: "pointer" }}
                            >
                              {item.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label small text-uppercase">
                        Industry
                      </label>
                      <Select
                        isMulti
                        closeMenuOnSelect={false} // keep open after selecting
                        hideSelectedOptions={true} // keep selected options hide
                        blurInputOnSelect={false} // keep cursor in search box
                        isClearable={true}
                        options={Industries}
                        value={selectedIndustry}
                        placeholder="Select Industry"
                        onChange={(selected) => {
                          setSelectedIndustry(selected || []);

                          setValue(
                            "industry",
                            selected ? selected.map((item) => item.value) : [],
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
                {activeTab === "search" && (
                  <div className="card p-4 mb-3" style={{ top: -23 }}>
                    <h6 className="fw-semibold mb-3">
                      <i className="ti ti-filter text-warning me-1" />
                      Filters
                    </h6>

                    <div className="mb-3">
                      <label className="form-label small text-uppercase">
                        Designation
                        {errors.designation && (
                          <span className="ms-1">
                            {errors.designation.message}
                          </span>
                        )}
                      </label>
                      <input
                        className="form-control"
                        type="text"
                        placeholder="Designation"
                        value={selectedDesignation || ""}
                        {...register("designation")}
                        onChange={(e) =>
                          handleDesignationChange(e.target.value)
                        }
                      />

                      {designationError && (
                        <small className="text-danger">
                          {designationError}
                        </small>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label small text-uppercase">
                        Function
                      </label>
                      <select
                        name="functions"
                        {...register("functions")}
                        className="form-select custom-functionselect"
                        onChange={(e) => {
                          setSelectedFunction(e.target.value);
                        }}
                      >
                        <option value="">Select Function</option>
                        {functions.length > 0
                          ? functions.map((value, index) => (
                              <option key={index} value={value.value}>
                                {value.label}
                              </option>
                            ))
                          : ""}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label small text-uppercase">
                        Country
                      </label>
                      <Select
                        isMulti
                        closeMenuOnSelect={false} // keep open after selecting
                        hideSelectedOptions={true} // keep selected options hide
                        blurInputOnSelect={false} // keep cursor in search box
                        isClearable={true}
                        options={country}
                        value={selectedCountry}
                        placeholder="Select Country"
                        onChange={(selected) => {
                          setSelectedCountry(selected || []);

                          setValue(
                            "country",
                            selected ? selected.map((item) => item.value) : [],
                          );
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label small text-uppercase">
                        Industry
                      </label>
                      <Select
                        isMulti
                        closeMenuOnSelect={false} // keep open after selecting
                        hideSelectedOptions={true} // keep selected options hide
                        blurInputOnSelect={false} // keep cursor in search box
                        isClearable={true}
                        options={Industries}
                        value={selectedIndustry}
                        placeholder="Select Industry"
                        onChange={(selected) => {
                          setSelectedIndustry(selected || []);

                          setValue(
                            "industry",
                            selected ? selected.map((item) => item.value) : [],
                          );
                        }}
                      />
                    </div>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        if (designationError) return; // prevent invalid search
                        setSearchTrigger((prev) => prev + 1);
                      }}
                    >
                      Search Candidates
                    </button>
                  </div>
                )}
              </aside>
            )}

            {/* MAIN */}
            <section className={showFilters ? "col-md-9" : "col-md-12"}>
              <div ref={filterRef} className="card p-4 mb-3">
                {/* Tabs */}
                <ul className="nav nav-pills nav-justified mb-4">
                  {[
                    ["jd", "Role Description", "ti-file-description"],
                    ["role", "Existing Role", "ti-briefcase"],
                    ["mapping", "Role Mappings", "ti-git-merge"],
                    // ["search", "Search Candidates", "ti-users"],
                  ].map(([key, label, icon]) => (
                    <li className="nav-item" key={key}>
                      <button
                        className={`nav-link ${
                          activeTab === key ? "active" : ""
                        }`}
                        onClick={() => {
                          // setShowFilters(key);
                          setActiveTab(key);
                        }}
                      >
                        <i className={`ti ${icon} me-1`} />
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* TAB CONTENT */}
                {activeTab === "jd" && (
                  <>
                    <JobDescription
                      submit={(e) => {
                        setGptCountry(e.country);
                        setGptIndustry(e.industry);
                        setGptFunction(e.function);
                        setGptDesignation(e.designation);
                        setGptRelatedCountries(e.related_countries);
                        setGptRelatedDesignations(e.related_designations);
                      }}
                      selectedDesignation={
                        selectedDesignation || gptDesignation
                      }
                      selectedCountry={selectedCountry}
                      selectedFunction={selectedFunction || gptFunction}
                      selectedIndustry={selectedIndustry}
                    />
                  </>
                )}

                {/* Existing Role */}

                {activeTab === "role" && (
                  <>
                    <JobDescription
                      mode="role"
                      submit={(e) => {
                        setGptCountry(e.country);
                        setGptIndustry(e.industry);
                        setGptFunction(e.function);
                        setGptDesignation(e.designation);
                        setGptRelatedCountries(e.related_countries);
                        setGptRelatedDesignations(e.related_designations);
                      }}
                      selectedDesignation={
                        selectedDesignation || gptDesignation
                      }
                      selectedCountry={selectedCountry}
                      selectedFunction={selectedFunction || gptFunction}
                      selectedIndustry={selectedIndustry}
                    />
                  </>
                )}

                {activeTab === "mapping" && (
                  <div className="mb-4">
                    <RoleMapping token={token} />
                  </div>
                )}

                {activeTab === "search" && (
                  <div className="mb-4">
                    <SearchCandidates
                      token={token}
                      searchTrigger={searchTrigger}
                      payload={{
                        designation: selectedDesignation || gptDesignation,

                        functions: Array.isArray(selectedFunction)
                          ? selectedFunction
                          : selectedFunction
                            ? [selectedFunction]
                            : gptFunction
                              ? Array.isArray(gptFunction)
                                ? gptFunction
                                : [gptFunction]
                              : [],

                        industries: selectedIndustry.map((item) => item.value),
                        countries: selectedCountry.map((item) => item.value),
                      }}
                    />
                  </div>
                )}
              </div>
            </section>
          </div>
          {activeTab !== "mapping" && (
            <button
              className="btn btn-primary position-fixed shadow"
              style={{
                top: "64px",
                left: "86px",
                zIndex: 100,
                borderRadius: "50%",
                width: "56px",
                height: "56px",
              }}
              onClick={() => setShowFilters((prev) => !prev)}
              title={showFilters ? "Close Filters" : "Open Filters"} // <-- Hover message
            >
              <i className={`ti ${showFilters ? "ti-x" : "ti-filter"}`} />
            </button>
          )}
        </main>
      </div>
    </>
  );
}
